import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import SearchIcon from "@mui/icons-material/Search";
import { ToastContainer } from "react-toastify";
import formatDate, {
  errorAlert,
  formatMonthDate,
} from "../../utils/services";
import axiosInstance from "../../helpers/axios";
import CustomDateRangePicker from "../../components/ui/CustomDateRangePicker";
import CustomTooltip from "../../components/ui/CustomTootltip";
import NoDataFound from "../../components/NoDataFound";
import TableSkeletonLoader from "../../components/ui/TableSkeletonLoader";

const FinanceReport = () => {
  const [startSelectedDate, setStartSelectedDate] = useState(() => {
    const today = new Date();
    const pastDate = new Date(today.setDate(today.getDate() - 7));
    return formatDate(pastDate);
  });
  const [endSelectedDate, setEndSelectedDate] = useState(
    formatDate(new Date())
  );
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: "",
    end: "",
  });
  const [loading, setLoading] = useState(true);
  const [reportRows, setReportRows] = useState([]);
  const [selectedMatterType, setSelectedMatterType] = useState("Fixed Fees");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined || amount === "") return "-";

    const value =
      typeof amount === "object" && amount.$numberDecimal !== undefined
        ? amount.$numberDecimal
        : amount;
    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) return value;

    return numericValue.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatHours = (hours) =>
    Number(hours || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatPeriod = (start, end) => {
    if (!start || !end) return "-";
    return `${formatMonthDate(start)} - ${formatMonthDate(end)}`;
  };

  useEffect(() => {
    const debounceTimer = setTimeout(
      () => setDebouncedSearchQuery(searchQuery),
      400
    );

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const visibleRows = useMemo(() => {
    const normalizedQuery = debouncedSearchQuery.trim().toLowerCase();

    return reportRows.filter((row) => {
      if (row.matterType !== selectedMatterType) return false;
      if (!normalizedQuery) return true;

      return [
        row.clientName,
        row.matterName,
        row.practiceArea,
        row.reviewer,
        row.remarks,
      ].some((value) =>
        String(value || "").toLowerCase().includes(normalizedQuery)
      );
    });
  }, [debouncedSearchQuery, reportRows, selectedMatterType]);
  const isFixedFeesTab = selectedMatterType === "Fixed Fees";
  const columnCount = isFixedFeesTab ? 13 : 11;

  const matterTypeCounts = useMemo(
    () =>
      reportRows.reduce(
        (counts, row) => ({
          ...counts,
          [row.matterType]: (counts[row.matterType] || 0) + 1,
        }),
        {}
      ),
    [reportRows]
  );

  const totals = useMemo(
    () =>
      visibleRows.reduce(
        (sum, row) => ({
          clockedAmount: sum.clockedAmount + Number(row.clockedAmount || 0),
          fixedFeesAmount:
            sum.fixedFeesAmount + Number(row.fixedFeesAmount || 0),
          unbilledAmount: sum.unbilledAmount + Number(row.unbilledAmount || 0),
          unutilizedAmount:
            sum.unutilizedAmount + Number(row.unutilizedAmount || 0),
          lastSentTSAmount:
            sum.lastSentTSAmount + Number(row.lastSentTSAmount || 0),
        }),
        {
          clockedAmount: 0,
          fixedFeesAmount: 0,
          unbilledAmount: 0,
          unutilizedAmount: 0,
          lastSentTSAmount: 0,
        }
      ),
    [visibleRows]
  );

  const escapeCsvValue = (value) =>
    `"${String(value ?? "").replace(/"/g, '""')}"`;

  const handleDownloadCsv = () => {
    const headers = [
      "Sr No.",
      "Client Name",
      "Matter Name",
      "Practice Area",
      "Reviewer",
      "Clocked Hours",
      "Clocked Amount",
      ...(isFixedFeesTab ? ["Total Fees"] : []),
      "Unbilled Amount",
      ...(isFixedFeesTab ? ["Unutilized Amount"] : []),
      "Last Sent TS Amount",
      "Last Sent TS Period",
      "Remark",
    ];

    const rows = visibleRows.map((row, index) => [
      index + 1,
      row.clientName || "-",
      row.matterName || "-",
      row.practiceArea || "-",
      row.reviewer || "-",
      formatHours(row.clockedHours),
      formatAmount(row.clockedAmount),
      ...(isFixedFeesTab ? [formatAmount(row.fixedFeesAmount)] : []),
      formatAmount(row.unbilledAmount),
      ...(isFixedFeesTab ? [formatAmount(row.unutilizedAmount)] : []),
      formatAmount(row.lastSentTSAmount),
      formatPeriod(row.lastSentTSPeriodStart, row.lastSentTSPeriodEnd),
      row.remarks || "-",
    ]);

    const totalRow = [
      "",
      "Total",
      "",
      "",
      "",
      "",
      formatAmount(totals.clockedAmount),
      ...(isFixedFeesTab ? [formatAmount(totals.fixedFeesAmount)] : []),
      formatAmount(totals.unbilledAmount),
      ...(isFixedFeesTab ? [formatAmount(totals.unutilizedAmount)] : []),
      formatAmount(totals.lastSentTSAmount),
      "",
      "",
    ];

    const csv = [headers, ...rows, totalRow]
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `finance-report-${selectedMatterType
      .toLowerCase()
      .replace(/\s+/g, "-")}-${startSelectedDate}-to-${endSelectedDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const fetchFinanceReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(
        "/workHour/getFinanceReportData",
        {
          params: {
            fromDate: startSelectedDate,
            toDate: endSelectedDate,
          },
        }
      );
      setReportRows(data.data || []);
    } catch (error) {
      errorAlert("Error fetching finance report data!");
    } finally {
      setLoading(false);
    }
  }, [startSelectedDate, endSelectedDate]);

  const handleDateRangeChange = (start, end) => {
    setSelectedDateRange({ start, end });
    setStartSelectedDate(start);
    setEndSelectedDate(end);
  };

  useEffect(() => {
    if (startSelectedDate && endSelectedDate) {
      fetchFinanceReport();
    }
  }, [startSelectedDate, endSelectedDate, fetchFinanceReport]);

  return (
    <>
      <ToastContainer />

      <Paper elevation={0} className="rounded-full">
        <Grid container alignItems="center" spacing={1} mb={2}>
          <Grid
            item
            xs={12}
            md={4}
            display="flex"
            alignItems="center"
            gap={0.5}
          >
            <Typography variant="h5">Finance Report</Typography>
            <Tooltip title="Displays active billable matters with period totals, unbilled amount, and latest TS tracker details.">
              <IconButton size="small">
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search client, matter, reviewer..."
              inputProps={{ "aria-label": "Search finance report" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4} display="flex" alignItems="center" gap={1}>
            <CustomTooltip text="Select Dates">
              <CustomDateRangePicker
                startDate={selectedDateRange.start}
                endDate={selectedDateRange.end}
                onDateRangeChange={handleDateRangeChange}
                iconSize={30}
              />
            </CustomTooltip>
            <Box display="flex" gap={1}>
              <Typography>{formatMonthDate(startSelectedDate)}</Typography>
              <Typography>-</Typography>
              <Typography>{formatMonthDate(endSelectedDate)}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "75vh" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            px={2}
            py={1}
          >
            <Typography variant="body2">
              FINANCE REPORT ({formatMonthDate(startSelectedDate)} -{" "}
              {formatMonthDate(endSelectedDate)})
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2">
                Active Matters: {visibleRows.length}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon fontSize="small" />}
                onClick={handleDownloadCsv}
                disabled={loading || visibleRows.length === 0}
              >
                CSV
              </Button>
            </Box>
          </Box>

          <Tabs
            value={selectedMatterType}
            onChange={(_, value) => setSelectedMatterType(value)}
            sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
          >
            <Tab
              value="Fixed Fees"
              label={`Fixed Fees (${matterTypeCounts["Fixed Fees"] || 0})`}
            />
            <Tab
              value="Time Spent"
              label={`Time Spent (${matterTypeCounts["Time Spent"] || 0})`}
            />
          </Tabs>

          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sr No.</TableCell>
                <TableCell>Client Name</TableCell>
                <TableCell>Matter Name</TableCell>
                <TableCell>Practice Area</TableCell>
                <TableCell>Reviewer</TableCell>
                <TableCell align="right">Clocked Hours</TableCell>
                <TableCell align="right">Clocked Amount</TableCell>
                {isFixedFeesTab && (
                  <TableCell align="right">Total Fees</TableCell>
                )}
                <TableCell align="right">Unbilled Amount</TableCell>
                {isFixedFeesTab && (
                  <TableCell align="right">Unutilized Amount</TableCell>
                )}
                <TableCell align="right">Last Sent TS Amount</TableCell>
                <TableCell>Last Sent TS Period</TableCell>
                <TableCell>Remark</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeletonLoader rows={6} colSpan={columnCount} />
              ) : visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnCount}>
                    <NoDataFound />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {visibleRows.map((row, index) => (
                    <TableRow key={row.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{row.clientName || "-"}</TableCell>
                      <TableCell>{row.matterName || "-"}</TableCell>
                      <TableCell>{row.practiceArea || "-"}</TableCell>
                      <TableCell>{row.reviewer || "-"}</TableCell>
                      <TableCell align="right">
                        {formatHours(row.clockedHours)}
                      </TableCell>
                      <TableCell align="right">
                        {formatAmount(row.clockedAmount)}
                      </TableCell>
                      {isFixedFeesTab && (
                        <TableCell align="right">
                          {formatAmount(row.fixedFeesAmount)}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {formatAmount(row.unbilledAmount)}
                      </TableCell>
                      {isFixedFeesTab && (
                        <TableCell align="right">
                          {formatAmount(row.unutilizedAmount)}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {formatAmount(row.lastSentTSAmount)}
                      </TableCell>
                      <TableCell>
                        {formatPeriod(
                          row.lastSentTSPeriodStart,
                          row.lastSentTSPeriodEnd
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        {row.remarks || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell />
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatAmount(totals.clockedAmount)}
                    </TableCell>
                    {isFixedFeesTab && (
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatAmount(totals.fixedFeesAmount)}
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatAmount(totals.unbilledAmount)}
                    </TableCell>
                    {isFixedFeesTab && (
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatAmount(totals.unutilizedAmount)}
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatAmount(totals.lastSentTSAmount)}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

export default FinanceReport;
